import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  getAuthTokenFromLocalStorage,
  getAuthUserFromLocalStorage,
  getUserMapperFromLocalStorage,
  removeAdminPortalEditModeValueFromLocalStorage,
  removeAuthTokenToLocalStorage,
  removeAuthUserToLocalStorage,
  removeUserMapperFromLocalStorage,
  setAuthTokenToLocalStorage,
  setAuthUserToLocalStorage,
  setUserMapperToLocalStorage,
} from '../../helper';
import { updateSubmissionCountdownSettings } from '../SubmissionSlice';
import { startTrial } from '../Trial';
import { IUser } from '../UserSlice';
import {
  getAuthUser,
  getClientCredentialsTokenV2,
  logInAsUser,
  userForgotPassword,
  userResetPassword,
  userSignIn,
  userSignUp,
} from './AuthApi';
import {
  addTutorExperience,
  addTutorQualification,
  addUserAchievement,
  addUserLanguage,
  addUserSubject,
  deleteTutorExperience,
  deleteTutorQualification,
  deleteUserAchievement,
  deleteUserLanguage,
  deleteUserSubject,
  updateTutorExperience,
  updateTutorQualification,
  updateUser,
  updateUserAchievement,
  updateUserLanguage,
  updateUserPageGuides,
} from '../UserSlice/UserApi';
import { saveUserDetails } from '../AccessLimitSlice/AccessLimitApi';
import { AUTH_TYPES } from '../../api';

export interface IUserWithMapper extends IUser {
  isPrimary?: boolean;
}

export interface IAuthState {
  status: 'idle' | 'loading' | 'rejected';
  accessToken: string | undefined;
  user: IUser | undefined;
  userMapper: IUserWithMapper[] | undefined;
}

const initialState: IAuthState = {
  status: 'idle',
  accessToken: getAuthTokenFromLocalStorage(),
  user: getAuthUserFromLocalStorage(),
  userMapper: getUserMapperFromLocalStorage(),
};

const handleRedirection = (oauthUser) => {
  const queryStringParams = new URLSearchParams(window.location.search);
  const redirect = queryStringParams.get('redirect') || '';
  const isTrialApply = queryStringParams.has('startTrial');

  let url = `${process.env.PUBLIC_URL}`;

  if (redirect) {
    url += redirect;
  }

  if (isTrialApply && oauthUser.isTrialApplied) {
    url += '?trialApplied';
  }

  if (oauthUser?.userMapper) {
    url = `${process.env.PUBLIC_URL}/manage-accounts`;
    window.location.href = `${url}/`;
  } else {
    window.location.href = redirect ? `${url}/` : `${url}/portal/dashboard`;
  }
};

export const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut: (state) => {
      removeAuthTokenToLocalStorage();
      removeAuthUserToLocalStorage();
      removeAdminPortalEditModeValueFromLocalStorage();
      removeUserMapperFromLocalStorage();
      state.status = 'idle';
      state.accessToken = undefined;
      state.user = undefined;
      window.location.href = `${process.env.PUBLIC_URL}/`;
    },
    setAuthUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // TRIAL
      .addCase(startTrial.fulfilled, (state, action) => {
        if (state.user) {
          const _user = action.payload;
          const _newStateUser = { ...state.user };
          _newStateUser.isQuiztopiyaPendingTrial =
            _user.isQuiztopiyaPendingTrial;
          _newStateUser.assessmentEngineDate = _user.assessmentEngineDate;
          _newStateUser.quiztopiyaTrialExpiration =
            _user.quiztopiyaTrialExpiration;

          state.user = _newStateUser;
          setAuthUserToLocalStorage(_newStateUser);
        }
      })
      // UPDATE SUBMISSION COUNTDOWN SETTINGS
      .addCase(updateSubmissionCountdownSettings.fulfilled, (state, action) => {
        if (state.user) {
          const _newStateUser = { ...state.user };
          if (action.payload.rememberOption) {
            _newStateUser.isContinueSubmissionWithTimerEnabled =
              action.payload.isTimerEnabled;
          }
          state.user = _newStateUser;
          setAuthUserToLocalStorage(_newStateUser);
        }
      })
      // SIGN UP
      .addCase(userSignUp.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(userSignUp.fulfilled, (state, action) => {
        state.status = 'idle';
        setAuthTokenToLocalStorage(action.payload.access_token);
        setAuthUserToLocalStorage(action.payload.oauthUser);
        state.accessToken = action.payload.access_token;
        state.user = action.payload.oauthUser;
        handleRedirection(action.payload.oauthUser);
      })
      .addCase(userSignUp.rejected, (state) => {
        state.status = 'rejected';
      })
      // SIGN IN
      .addCase(userSignIn.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(userSignIn.fulfilled, (state, action) => {
        state.status = 'idle';

        setAuthTokenToLocalStorage(action.payload.access_token);
        setAuthUserToLocalStorage(action.payload.oauthUser);

        state.accessToken = action.payload.access_token;
        state.user = action.payload.oauthUser;

        if (action.payload.oauthUser?.userMapper) {
          setUserMapperToLocalStorage(action.payload.oauthUser.userMapper);
          state.userMapper = action.payload.oauthUser.userMapper;
        } else {
          removeUserMapperFromLocalStorage();
          state.userMapper = undefined;
        }

        if (action.meta.arg.auth_type !== AUTH_TYPES.GUEST) {
          handleRedirection(action.payload.oauthUser);
        }
      })
      .addCase(userSignIn.rejected, (state, action) => {
        state.status = 'rejected';
        removeAuthTokenToLocalStorage();
        removeAuthUserToLocalStorage();
        state.accessToken = undefined;
        state.user = undefined;
      })
      // CLIENT TOKEN V2
      .addCase(getClientCredentialsTokenV2.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getClientCredentialsTokenV2.fulfilled, (state, action) => {
        state.status = 'idle';
        setAuthTokenToLocalStorage(action.payload.access_token);
        state.accessToken = action.payload.access_token;
      })
      .addCase(getClientCredentialsTokenV2.rejected, (state, action) => {
        state.status = 'rejected';
        removeAuthTokenToLocalStorage();
        state.accessToken = undefined;
      })
      // GET AUTH USER
      .addCase(getAuthUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAuthUser.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(getAuthUser.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;
        if (result) {
          setAuthUserToLocalStorage(result);
          state.user = result;
        }
      })

      // LOG IN AS USER
      .addCase(logInAsUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(logInAsUser.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(logInAsUser.fulfilled, (state, action) => {
        state.status = 'idle';
        setAuthUserToLocalStorage(action.payload.user);
        setUserMapperToLocalStorage(action.payload.userMapper);
        state.user = action.payload.user;
        state.userMapper = action.payload.userMapper;
        window.location.reload();
      })

      // FORGOT PASSWORD
      .addCase(userForgotPassword.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(userForgotPassword.fulfilled, (state, action) => {
        state.status = 'idle';
        const email = action.payload?.email;
        const isResetPasswordPage = window.location.pathname.includes(
          '/reset-password'
        );

        if (email && !isResetPasswordPage) {
          const params = new URLSearchParams({ email });
          const redirectUrl = `${
            process.env.PUBLIC_URL
          }/reset-password?${params.toString()}`;

          window.location.href = redirectUrl;
        }
      })
      .addCase(userForgotPassword.rejected, (state) => {
        state.status = 'rejected';
      })

      // RESET PASSWORD
      .addCase(userResetPassword.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(userResetPassword.fulfilled, (state) => {
        state.status = 'idle';
        window.location.href = `${process.env.PUBLIC_URL}/login?resetSuccess`;
      })
      .addCase(userResetPassword.rejected, (state) => {
        state.status = 'rejected';
      })

      // MATCHER
      .addMatcher(
        (action) =>
          action.type === updateUser.fulfilled.type ||
          action.type === addTutorExperience.fulfilled.type ||
          action.type === updateTutorExperience.fulfilled.type ||
          action.type === deleteTutorExperience.fulfilled.type ||
          action.type === addTutorQualification.fulfilled.type ||
          action.type === updateTutorQualification.fulfilled.type ||
          action.type === deleteTutorQualification.fulfilled.type ||
          action.type === addUserAchievement.fulfilled.type ||
          action.type === updateUserAchievement.fulfilled.type ||
          action.type === deleteUserAchievement.fulfilled.type ||
          action.type === addUserLanguage.fulfilled.type ||
          action.type === updateUserLanguage.fulfilled.type ||
          action.type === deleteUserLanguage.fulfilled.type ||
          action.type === addUserSubject.fulfilled.type ||
          action.type === deleteUserSubject.fulfilled.type ||
          action.type === updateUserPageGuides.fulfilled.type ||
          action.type === saveUserDetails.fulfilled.type,
        (state, action) => {
          setAuthUserToLocalStorage(action.payload);
          state.user = { ...state.user, ...action.payload };
        }
      );
  },
});

export const { signOut, setAuthUser } = AuthSlice.actions;

export const getAuthState = (state: RootState): RootState['auth'] => state.auth;

export * from './AuthApi';
export default AuthSlice.reducer;
